/**
 * Statistics Dashboard - Main Component (Phase 1)
 * 
 * Complete dashboard with all 6 widgets:
 * 1. Overall Progress Block
 * 2. Letter Performance Matrix  
 * 3. Daily Progress Trends
 * 4. Finger Performance Analysis
 * 5. Weak Spots Detector
 * 6. Training Calendar Heatmap
 */

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useStatistics, useTimePeriod } from '@/hooks/useStatistics';
import { TimePeriod, CustomPeriod } from '@/types/statistics';

// Import all widget components
import OverallStatsBlock from './OverallStatsBlock';
import LetterPerformanceMatrix from './LetterPerformanceMatrix'; 
import ProgressTrendsChart from './ProgressTrendsChart';
import FingerPerformanceBlock from './FingerPerformanceBlock';
import WeakSpotsBlock from './WeakSpotsBlock';
import TrainingCalendarHeatmap from './TrainingCalendarHeatmap';

const StatisticsDashboard: React.FC = () => {
  const { user } = useAuth();
  const { period, customPeriod, updatePeriod } = useTimePeriod('month');
  const { data, loading, error, refetch, isInitialLoad } = useStatistics(
    user?.uid || '', 
    period, 
    customPeriod
  );
  
  // Loading states for different widgets
  const isLoading = isInitialLoad || loading.overall;
  
  // Smooth transition state for period changes
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Handle period change with smooth transition
  const handlePeriodChange = (newPeriod: TimePeriod, custom?: CustomPeriod) => {
    setIsTransitioning(true);
    updatePeriod(newPeriod, custom);
    // Reset transition after data loads
    setTimeout(() => setIsTransitioning(false), 300);
  };
  
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            🔐 Please Sign In
          </h2>
          <p className="text-gray-600">
            Sign in to view your typing statistics and progress.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            ❌ Error Loading Statistics
          </h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={refetch}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    // FIX 1: SCROLLING FIX - Changed minHeight to height to create a fixed viewport boundary.
    <div style={{ 
      height: '100vh',
      background: '#0f172a',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header Section - Unchanged */}
      <header style={{ 
        background: '#1e293b', 
        borderBottom: '1px solid #334155', 
        padding: '20px 0',
        flexShrink: 0
      }}>
        <div style={{ 
          margin: '0 auto', 
          padding: '0 20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <TimePeriodSelector 
            period={period}
            customPeriod={customPeriod}
            onPeriodChange={handlePeriodChange}
            loading={isLoading}
          />
        </div>
      </header>

      {/* FIX 1: SCROLLING FIX - Added minHeight: 0 for flexbox robustness. */}
      <main style={{ 
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0
      }}>
        <div style={{
          margin: '0 auto', 
          padding: '20px',
          minHeight: '100%'
        }}>
        {isLoading ? (
          <LoadingState />
        ) : (
          // FIX 2: LAYOUT FIX - The `style` prop was removed to allow the CSS class to control the grid.
          <div className="statistics-grid" style={{ opacity: isTransitioning ? 0.7 : 1 }}>
            
            {/* FIX 2: LAYOUT FIX - Removed all `style={{ gridColumn: '...' }}` props from these divs. */}
            {/* The grid will now automatically place each widget. */}
            
            <div>
              <OverallStatsBlock 
                data={data?.overall || null}
                loading={loading.overall}
                period={period}
              />
            </div>
            
            <div>
              <ProgressTrendsChart 
                data={data?.dailyProgress || []}
                loading={loading.trends}
                period={period}
              />
            </div>
            
            <div>
              <LetterPerformanceMatrix 
                data={data?.letters || []}
                loading={loading.letters}
                period={period}
              />
            </div>
            
            <div>
              <FingerPerformanceBlock 
                data={data?.fingerPerformance || []}
                loading={loading.fingers}
              />
            </div>
            
            <div>
              <WeakSpotsBlock 
                data={data?.weakSpots || []}
                loading={loading.weakSpots}
              />
            </div>
            
            <div>
              <TrainingCalendarHeatmap 
                data={data?.calendarData || []}
                loading={loading.calendar}
              />
            </div>
            
          </div>
        )}
        </div>
      </main>
      
      {/* Footer - Unchanged */}
      <footer style={{ 
        background: '#1e293b', 
        borderTop: '1px solid #334155', 
        padding: '16px 0',
        flexShrink: 0
      }}>
        <div style={{ 
          margin: '0 auto', 
          padding: '0 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>
            Statistics updated in real-time • Data from your practice sessions
          </p>
          <button
            onClick={refetch}
            disabled={isLoading}
            style={{ 
              fontSize: '12px', 
              color: '#3b82f6', 
              fontWeight: '500',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              opacity: isLoading ? 0.5 : 1,
              margin: 0
            }}
          >
            {isLoading ? '🔄 Updating...' : '🔄 Refresh Data'}
          </button>
        </div>
      </footer>
      
      {/* FIX 2: LAYOUT FIX - Replaced the entire CSS with a simpler, more robust version. */}
      <style>{`
        .statistics-grid {
          display: grid;
          /* BASE: A simple 3-column grid for a balanced look */
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          width: 100%;
        }
        
        /* RESPONSIVE BREAKPOINTS */
        
        /* Medium screens (e.g., small laptops) -> 2 columns */
        @media (max-width: 1200px) {
          .statistics-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        /* Small screens (e.g., tablets) -> 1 column */
        @media (max-width: 768px) {
          .statistics-grid {
            grid-template-columns: 1fr;
          }
        }
        
        /* Ensure all widgets have consistent height and appearance */
        .statistics-grid > div {
          min-height: 300px;
          display: flex;
          flex-direction: column;
        }
        
        /* Smooth transitions for period changes */
        .statistics-grid {
          transition: opacity 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// Time Period Selector Component - No changes needed here
const TimePeriodSelector: React.FC<{
  period: TimePeriod;
  customPeriod?: CustomPeriod;
  onPeriodChange: (period: TimePeriod, custom?: CustomPeriod) => void;
  loading: boolean;
}> = ({ period, onPeriodChange, loading }) => {
  
  const periods = [
    { value: 'week' as TimePeriod, label: 'Last Week', icon: '📅' },
    { value: 'month' as TimePeriod, label: 'Last Month', icon: '📊' },
    { value: 'all' as TimePeriod, label: 'All Time', icon: '🏆' }
  ];
  
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      {periods.map(({ value, label, icon }) => (
        <button
          key={value}
          onClick={() => onPeriodChange(value)}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '14px',
            border: '1px solid',
            borderColor: period === value ? '#3b82f6' : '#475569',
            background: period === value ? 'linear-gradient(45deg, #3b82f6, #2563eb)' : '#334155',
            color: period === value ? '#ffffff' : '#e2e8f0',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
            transition: 'all 0.2s ease-out',
            transform: 'translateY(0px)',
            boxShadow: period === value ? '0 4px 12px rgba(59, 130, 246, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            if (!loading && period !== value) {
              e.currentTarget.style.background = '#475569';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && period !== value) {
              e.currentTarget.style.background = '#334155';
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }
          }}
        >
          <span>{icon}</span>
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
};

// Loading State Component - No changes needed here
const LoadingState: React.FC = () => (
  <div className="space-y-8 animate-pulse">
    {/* Overall Stats Skeleton */}
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="h-6 bg-gray-200 rounded-md w-48 mb-6"></div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-4">
            <div className="h-8 bg-gray-200 rounded-md mb-2"></div>
            <div className="h-4 bg-gray-200 rounded-md w-16 mb-1"></div>
            <div className="h-6 bg-gray-200 rounded-md w-20"></div>
          </div>
        ))}
      </div>
    </div>
    
    {/* Charts Skeleton */}
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="h-6 bg-gray-200 rounded-md w-40 mb-4"></div>
        <div className="h-80 bg-gray-100 rounded-lg"></div>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="h-6 bg-gray-200 rounded-md w-40 mb-4"></div>
        <div className="h-80 bg-gray-100 rounded-lg"></div>
      </div>
    </div>
  </div>
);

// Note: Your original code had an unused AchievementSection component. 
// I've left it out for brevity, but it can be added back if needed.

// Achievement Section Component - Commented out as unused
// const _AchievementSection: React.FC<{
//   achievements: any[];
// }> = ({ achievements }) => {
//   const recentAchievements = achievements.filter(a => a.achieved).slice(0, 3);
//   const nextAchievements = achievements.filter(a => !a.achieved).slice(0, 3);
//
//   if (recentAchievements.length === 0 && nextAchievements.length === 0) {
//     return null;
//   }
//
//   return (
//     <div className="bg-white rounded-xl shadow-sm p-6">
//       <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
//         🏆 Achievements
//       </h2>
//
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//         {/* Recent Achievements */}
//         {recentAchievements.length > 0 && (
//           <div>
//             <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
//               ✅ Recently Unlocked
//             </h3>
//             <div className="space-y-2">
//               {recentAchievements.map(achievement => (
//                 <div
//                   key={achievement.id}
//                   className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100"
//                 >
//                   <span className="text-2xl">{achievement.icon}</span>
//                   <div>
//                     <div className="font-medium text-green-800">{achievement.title}</div>
//                     <div className="text-sm text-green-600">{achievement.description}</div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}
//
//         {/* Next Achievements */}
//         {nextAchievements.length > 0 && (
//           <div>
//             <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
//               🎯 Coming Up Next
//             </h3>
//             <div className="space-y-2">
//               {nextAchievements.map(achievement => (
//                 <div
//                   key={achievement.id}
//                   className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"
//                 >
//                   <span className="text-2xl opacity-60">{achievement.icon}</span>
//                   <div className="flex-1">
//                     <div className="font-medium text-blue-800">{achievement.title}</div>
//                     <div className="text-sm text-blue-600 mb-1">{achievement.description}</div>
//                     <div className="w-full bg-blue-200 rounded-full h-2">
//                       <div
//                         className="bg-blue-500 h-2 rounded-full transition-all duration-300"
//                         style={{ width: `${achievement.progress}%` }}
//                       />
//                     </div>
//                     <div className="text-xs text-blue-600 mt-1">
//                       {achievement.progress}% complete
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

export default StatisticsDashboard;